import React, { FC, ReactNode, useEffect } from "react";
import Head from "next/head";
import useSeoContent from "src/hooks/useSeoContent";
import { useRouter } from "next/router";
import { localeDomainMapV2 } from "src/utils/domains";
import { buildHrefLink, parsePathInfo } from "src/modules/page/utils";
interface Props {
  title?: string;
  deeplink?: string;
  metaTags?: ReactNode;
  description?: string;
  scripts?: ReactNode;
  noindex?: boolean;
  isPDP?: boolean;
  enableIndexation?: boolean;
  skipSeoContent?: boolean;
}

const PageHead: FC<Props> = ({
  title,
  deeplink,
  metaTags,
  description,
  noindex = true,
  scripts,
  isPDP,
  enableIndexation,
  skipSeoContent = false,
}) => {
  const metaRobotsContent = `follow${noindex ? ",noindex" : ""}`;
  const router = useRouter();
  let pageUrlSlug = router.asPath;
  if (pageUrlSlug.includes("?")) {
    pageUrlSlug = pageUrlSlug.slice(0, pageUrlSlug.indexOf("?")); // Remove query params
  }
  const { data } = useSeoContent({
    page_url_slug: pageUrlSlug,
    skipSeoContent,
  });
  const seoContent = data?.getSeoContent;

  // Parse path information
  const pathInfo = parsePathInfo(router.asPath);
  const localesUsingDomainAsHreflang = new Set(["pl-pl", "es-es", "it-it"]);
  const hrefLangs = Object.entries(localeDomainMapV2).map(
    ([locale, domain]) => {
      const hrefLink = buildHrefLink(pathInfo, [locale, domain]);
      if (localesUsingDomainAsHreflang.has(locale)) {
        locale = domain; // Use domain as locale for specific languages
      }
      return (
        <link rel="alternate" href={hrefLink} hrefLang={locale} key={locale} />
      );
    }
  );

  const robotsTagValue = seoContent
    ? seoContent?.robots_tag_value
    : isPDP || enableIndexation
    ? "follow,index"
    : metaRobotsContent;

  const removeMetaTags = (selector: string) => {
    const existingCanonicals = document.querySelectorAll(selector);
    existingCanonicals.forEach((el) => el.remove());
  };

  useEffect(() => {
    if (seoContent?.canonical_tags) {
      removeMetaTags('link[rel="canonical"]');
    }
    if (seoContent?.meta_description) {
      removeMetaTags('meta[name="description"]');
      removeMetaTags('meta[property="og:description"]');
    }
  }, [seoContent]);

  return (
    <Head>
      <title>{seoContent?.meta_title || title}</title>
      <meta
        name="description"
        content={seoContent?.meta_description || description}
      />
      <meta
        property="og:description"
        content={seoContent?.meta_description || description}
      />
      {seoContent?.canonical_tags && (
        <link rel="canonical" href={seoContent.canonical_tags} />
      )}
      {robotsTagValue && <meta name="robots" content={robotsTagValue} />}
      <meta
        name="og:image"
        content="https://www.otrium.nl/wp-content/themes/otrium/images/otrium_nl_2.png"
      />
      {deeplink && (
        <>
          <meta property="al:android:url" content={deeplink} />
          <meta property="al:ios:url" content={deeplink} />
        </>
      )}
      {metaTags && (
        <>
          <React.Fragment>{metaTags}</React.Fragment>
        </>
      )}
      {hrefLangs}
      {scripts && scripts}
    </Head>
  );
};
export default PageHead;
